import { ReactNode, useEffect, useRef, useState } from 'react';
import { Icon } from '../../utils/icons';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  maxHeight?: string;
}

export default function BottomSheet({ 
  isOpen, 
  onClose, 
  children, 
  title,
  maxHeight = '90vh'
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const startY = useRef<number>(0);
  const currentY = useRef<number>(0);
  const isDragging = useRef(false);
  const scrollY = useRef(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      scrollY.current = window.scrollY || window.pageYOffset;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY.current}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      // Reset drag offset and fullscreen state when opening
      setDragOffset(0);
      setIsFullscreen(false);
    } else {
      const restoreY = scrollY.current;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, restoreY);
      // Reset drag offset and fullscreen state when closing
      setDragOffset(0);
      setIsFullscreen(false);
    }
    return () => {
      const restoreY = scrollY.current;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, restoreY);
    };
  }, [isOpen]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!sheetRef.current) return;
    const touch = e.touches[0];
    startY.current = touch.clientY;
    currentY.current = touch.clientY;
    isDragging.current = true;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !sheetRef.current) return;
    const touch = e.touches[0];
    currentY.current = touch.clientY;
    const deltaY = currentY.current - startY.current;
    
    // Allow both upward (expand) and downward (close) drag
    if (deltaY !== 0) {
      e.preventDefault();
      e.stopPropagation();
      
      // Use requestAnimationFrame for smooth updates
      requestAnimationFrame(() => {
        // For upward drag (negative deltaY), expand to fullscreen
        if (deltaY < 0) {
          setIsFullscreen(true);
          setDragOffset(0); // Reset drag offset when expanding
        } else {
          // For downward drag, show the drag offset
          setIsFullscreen(false);
          setDragOffset(deltaY);
        }
      });
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current || !sheetRef.current) return;
    isDragging.current = false;
    
    const deltaY = currentY.current - startY.current;
    const closeThreshold = 100; // Minimum drag distance to close
    const expandThreshold = -50; // Minimum drag distance to expand
    const collapseThreshold = 50; // Minimum drag distance to collapse from fullscreen
    
    if (isFullscreen && deltaY > collapseThreshold) {
      // Collapse from fullscreen to normal view
      requestAnimationFrame(() => {
        setIsFullscreen(false);
        setDragOffset(0);
      });
    } else if (deltaY > closeThreshold) {
      // Close the sheet
      onClose();
    } else if (deltaY < expandThreshold && !isFullscreen) {
      // Expand to fullscreen
      requestAnimationFrame(() => {
        setIsFullscreen(true);
        setDragOffset(0);
      });
    } else {
      // Snap back to previous state
      requestAnimationFrame(() => {
        setDragOffset(0);
        // If was expanding but didn't reach threshold, stay in current state
        if (deltaY < 0 && !isFullscreen) {
          setIsFullscreen(false);
        }
      });
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity"
        onClick={onClose}
      />
      
      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className="fixed bottom-0 left-0 right-0 bg-white rounded-landing-t shadow-2xl z-[70] overflow-hidden flex flex-col safe-area-inset-bottom"
        style={{ 
          height: isFullscreen ? '100vh' : undefined,
          maxHeight: isFullscreen ? '100vh' : maxHeight,
          transform: `translateY(${dragOffset}px)`,
          willChange: 'transform',
          transition: dragOffset < 5 && dragOffset > -5 ? 'transform 0.2s ease-out, height 0.2s ease-out, maxHeight 0.2s ease-out' : 'none',
        }}
      >
        {/* Drag Handle */}
        <div 
          className="flex justify-center items-center py-4 touch-none min-h-[60px]"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ touchAction: 'none' }}
        >
          <div className="w-12 h-1 bg-gray-300 rounded-sm-full" />
        </div>
        
        {/* Title */}
        {title && (
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            {isFullscreen && (
              <button
                onClick={() => {
                  onClose();
                }}
                className="px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation min-h-[44px] flex items-center justify-center gap-2"
                title="Close"
                aria-label="Close bottom sheet"
              >
                <Icon name="close" size="md" className="text-gray-700" />
                <span className="text-sm font-medium text-gray-700">Close</span>
              </button>
            )}
          </div>
        )}
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto scrollable" style={{ overscrollBehavior: 'contain' }}>
          {children}
        </div>
      </div>
    </>
  );
}
